import type { Point } from '../../../types';
import { DrawShape } from './DrawShape';
import type { DrawShapeStyle, Size } from './types';

export class DrawEllipse extends DrawShape {
  readonly type = 'ellipse' as const;

  constructor(
    id: string,
    position: Point,
    size: Size,
    style: DrawShapeStyle
  ) {
    super(id, position, size, style);
  }

  containsPoint(p: Point): boolean {
    const center = this.getCenter();
    const rx = this.size.width / 2;
    const ry = this.size.height / 2;
    const threshold = 10;

    const dx = p.x - center.x;
    const dy = p.y - center.y;

    // Check if point is within the expanded outer ellipse
    const outerRx = rx + threshold;
    const outerRy = ry + threshold;
    const inExpandedOuter = (dx * dx) / (outerRx * outerRx) + (dy * dy) / (outerRy * outerRy) <= 1;
    if (!inExpandedOuter) return false;

    // Check if point is deep inside (beyond the inner grab zone)
    const innerRx = Math.max(0, rx - threshold);
    const innerRy = Math.max(0, ry - threshold);
    const insideInner = (dx * dx) / (innerRx * innerRx) + (dy * dy) / (innerRy * innerRy) <= 1;

    // Return true if in the border zone (expanded outer but not deep inner)
    return !insideInner;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const center = this.getCenter();
    const rx = this.size.width / 2;
    const ry = this.size.height / 2;
    const { fill, stroke, strokeWidth, opacity } = this.style;

    ctx.save();
    ctx.globalAlpha = opacity;

    ctx.beginPath();
    ctx.ellipse(center.x, center.y, rx, ry, 0, 0, Math.PI * 2);
    ctx.closePath();

    // Fill
    if (fill && fill !== 'transparent') {
      ctx.fillStyle = fill;
      ctx.fill();
    }

    // Stroke
    if (stroke && strokeWidth > 0) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = strokeWidth;
      ctx.stroke();
    }

    ctx.restore();
  }

  static fromJSON(data: { id: string; position: Point; size: Size; style: DrawShapeStyle }): DrawEllipse {
    return new DrawEllipse(data.id, data.position, data.size, data.style);
  }
}
